import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import C3 from "c3";
import { createTestModel } from "../../../../../webapi/service/createTestModel";
import {
  ICreateModelPanelProps,
  ILabelSelectItem,
  createLabelSelectItems,
  collectLabelFileids,
  changeLabelSelectItemValue,
  changeAllLabelSelectItemValue,
  IOptionItems,
  getOptionItems,
  getSelectList,
} from "./CreateModelPanel";
import ModelListPanel from "./ModelListPanel";
import {
  drawCharts,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
  getSortProts,
} from "../common/chart";
import { ProjectStatus, ChartList } from "../../../../../common/constants";
import { IProtRow } from "../../../../../webapi/service/getFIles";
import { ModelSelect } from "../../../../../common/constants";
import "../../projectdetail.css";

const McreateUnused = ProjectStatus.NONE;

function isCreateEnabled(selectItems: ILabelSelectItem[]): boolean {
  return selectItems.find((item) => {
    return item.value === ModelSelect.OK || item.value === ModelSelect.NG;
  })
    ? true
    : false;
}

function createChartTable(
  prots: IProtRow[],
  selectItems: ILabelSelectItem[],
  chartidFmt: string,
  optionItems: IOptionItems[],
  usedString: string,
  chartWidth: number,
  isLoading: boolean,
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
): JSX.Element[] {
  const options = getOptionItems(optionItems);
  const charts = prots.map((prot, i) => {
    const { id, fname, mcreatelabel } = prot;
    const chartid: string = chartidFmt + String(id);
    const formselect = getSelectList(id, selectItems, options, onChange);
    const isUsed = mcreatelabel !== McreateUnused;
    const listItemClass = isUsed ? "chart-list-item-used" : "chart-list-item";
    const label = isUsed ? fname + "(" + usedString + ")" : fname;
    return (
      <div className={listItemClass} key={i}>
        <div
          className="chart-list-test-fname"
          style={{ width: chartWidth, display: isLoading ? "none" : undefined }}
        >
          {label}
        </div>
        <div id={chartid}></div>
        <div className="chart-list-select">{formselect}</div>
      </div>
    );
  });
  return charts;
}

export interface IProps extends ICreateModelPanelProps, WithTranslation {
  /**
   * ローディング中の場合はtrue
   */
  isLoading: boolean;
}

interface IState {
  /**
   * チャート一覧
   */
  selectItems: ILabelSelectItem[];
  /**
   * テストモデルが作成可能な場合はtrue
   */
  create: boolean;
}

class TestModelPanel extends React.Component<IProps, IState> {
  private readonly chartid_fmt = "chart_test_model_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.TEST_MODEL_WIDTH,
    height: ChartList.TEST_MODEL_HEIGHT,
  };
  private readonly MaxDataNum = ChartList.MAX_DATA_NUM;
  private readonly StatusDone = ProjectStatus.DONE;
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;

  constructor(props: IProps) {
    super(props);

    this.state = {
      selectItems: [],
      create: false,
    };

    this.handleCreate = this.handleCreate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleChangeBulk = this.handleChangeBulk.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(prots: IProtRow[]): void {
    const { mainSpaceWidth } = this.props;
    const testModelProts: IProtRow[] = prots.map((prot) => {
      return {
        id: prot.id,
        fileid: prot.fileid,
        fname: "",
        datanum: prot.datanum,
        data: prot.data,
        mcreatelabel: prot.mcreatelabel,
      };
    });
    const { chartWidth, chartHeight } = getChartTableInfo(
      mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(
      testModelProts,
      this.chartid_fmt,
      chartWidth,
      chartHeight
    );
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  getPros() {
    const { prots, sortIds } = this.props;
    return getSortProts(prots, sortIds).filter((prot) => {
      return prot.datanum <= this.MaxDataNum;
    });
  }

  componentDidMount() {
    const { onLoading } = this.props;
    onLoading(true);
    setTimeout(() => {
      const prots = this.getPros();
      this.createCharts(prots);
      this.setState({ selectItems: createLabelSelectItems(prots) });
      onLoading(false);
    });
  }

  componentWillUnmount() {
    this.destroyCharts();
  }

  handleCreate() {
    const { projectInfo, showConfirm, t } = this.props;
    if (projectInfo.mteststs === this.StatusDone) {
      showConfirm({
        title: t("model_test_overwrite"),
        message: t("model_test_overwrite_message"),
        onOk: () => {
          this.createTestModel();
        },
      });
    } else {
      this.createTestModel();
    }
  }

  createTestModel() {
    const {
      authService,
      projectInfo,
      onLoading,
      onTop,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    onLoading(true);
    const { okfilesids, ngfilesids } = collectLabelFileids(
      this.state.selectItems
    );
    resetUnoperatedTimeout();
    createTestModel({
      authService: authService,
      projectid: projectInfo.projectid,
      okfiles: okfilesids,
      ngfiles: ngfilesids,
      success: () => {
        onTop();
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const id = e.target.id;
    const value: number = parseInt(e.target.value);
    const selectItems = changeLabelSelectItemValue(
      this.state.selectItems.slice(),
      id,
      value
    );
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  handleChangeBulk(mode: number) {
    const selectItems = changeAllLabelSelectItemValue(
      this.state.selectItems.slice(),
      mode
    );
    this.setState({
      selectItems: selectItems,
      create: isCreateEnabled(selectItems),
    });
  }

  render() {
    const { mainSpaceWidth, isLoading, onBack, t } = this.props;
    const { selectItems, create } = this.state;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const optionItems: IOptionItems[] = [
      { text: t("model_test_settings_ok"), value: ModelSelect.OK },
      { text: t("model_test_settings_ng"), value: ModelSelect.NG },
      {
        text: t("model_test_settings_unused"),
        value: ModelSelect.UNUSED,
      },
    ];
    const contents = createChartTable(
      this.getPros(),
      selectItems,
      this.chartid_fmt,
      optionItems,
      t("model_test_used"),
      this.chartSize().width,
      isLoading,
      this.handleChange
    );
    return (
      <React.Fragment>
        <ModelListPanel
          label={t("model_test")}
          chartTableStyle={chartTableStyle}
          chartTable={contents}
          isCreateEnabled={create}
          onBack={onBack}
          onCreate={this.handleCreate}
          onChangeAll={this.handleChangeBulk}
        />
      </React.Fragment>
    );
  }
}

/**
 * テストモデル画面
 */
export default withTranslation()(TestModelPanel);
